/**
 * DocViewerErrorBoundary — catches BOTH a failed lazy-chunk fetch
 * (the stale-deploy / network-blip case that survived retryImport's
 * one retry) AND any runtime crash inside a mounted viewer, and
 * renders a recovery panel instead of an unmounted-tree blank.
 *
 * Three recovery paths, mapping to the three real ways out:
 *   - Refresh page      → pulls the current bundle (fixes stale chunks).
 *   - Download original → save the file and open it locally.
 *   - View extracted text → show the plain text the LLM actually sees,
 *     which is independent of the viewer chunk entirely.
 *
 * `resetKey` (the active doc id) lets the boundary recover when the
 * user closes the dialog and opens a different doc — without it, a
 * caught error would stick until a full reload.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Download, FileText } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { downloadOriginal } from './downloadOriginal';
import type { SiteDocument } from '../../../types/siteRag';

interface Props {
  doc: SiteDocument;
  /** Changing this value (e.g. the doc id) clears a previously-caught error. */
  resetKey: string;
  /** Opens the "what the AI sees" extracted-text view. */
  onViewExtractedText: (doc: SiteDocument) => void;
  onDownloadOriginal?: (doc: SiteDocument) => Promise<void>;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
  /** Snapshot of the resetKey at the time the error was caught. */
  errorKey: string | null;
}

export class DocViewerErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: null, errorKey: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error?.message ?? 'Unknown error' };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // The doc changed (dialog reopened on a different file) — drop the
    // stale error so the new viewer gets a clean mount.
    if (state.hasError && state.errorKey !== null && state.errorKey !== props.resetKey) {
      return { hasError: false, message: null, errorKey: null };
    }
    // First render after an error: remember which doc it happened on.
    if (state.hasError && state.errorKey === null) {
      return { errorKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface to the console so a minified prod stack is still
    // diagnosable via the sourcemap recipe.
    console.error('[DocViewerErrorBoundary] viewer failed', {
      file: this.props.doc.fileName,
      message: error?.message,
      componentStack: info?.componentStack,
    });
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { doc, onViewExtractedText } = this.props;
    const isChunkError = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
      this.state.message ?? '',
    );
    return (
      <div className="h-full w-full flex items-center justify-center p-6" role="alert" data-testid="doc-viewer-error-boundary">
        <div className="rounded-md border border-warning/40 bg-warning/5 px-6 py-8 text-center space-y-4 max-w-md">
          <AlertTriangle className="w-8 h-8 text-warning mx-auto" />
          <div className="space-y-1">
            <div className="text-sm font-bold text-text-primary">This viewer couldn't load.</div>
            <p className="text-xs text-text-secondary">
              {isChunkError
                ? 'A newer version of Datacrate was deployed since you opened this tab — refresh to load it. Or download the file / view the extracted text below.'
                : 'Something went wrong rendering this file. You can still download the original or view the text the AI uses.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button size="sm" className="text-xs" onClick={() => location.reload()} data-testid="doc-viewer-error-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh page
            </Button>
            <Button
              size="sm" variant="outline" className="text-xs"
              disabled={!doc.rawStoragePath}
              title={doc.rawStoragePath ? 'Download the original file' : 'Re-upload to enable download'}
              onClick={async () => {
                try { await (this.props.onDownloadOriginal ?? downloadOriginal)(doc); }
                catch (e) { toast.error((e as Error).message || 'Download failed'); }
              }}
              data-testid="doc-viewer-error-download"
            >
              <Download className="w-4 h-4 mr-1" /> Download original
            </Button>
            <Button
              size="sm" variant="outline" className="text-xs"
              disabled={!doc.textContent?.trim()}
              title={doc.textContent?.trim() ? 'Show the extracted text the AI sees' : 'No extracted text available for this file'}
              onClick={() => onViewExtractedText(doc)}
              data-testid="doc-viewer-error-extracted"
            >
              <FileText className="w-4 h-4 mr-1" /> View extracted text
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
